import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface Member {
  uuid: string;
  user: {
    uuid: number;
    username: string;
    email: string;
  };
  role: string;
  status: string;
  joined_at: string;
}

interface EditMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  selectedMember: Member | null;
  editRole: string;
  setEditRole: (role: string) => void;
  canManageManagers: boolean;
  currentUserId: number;
}

const EditMemberDialog: React.FC<EditMemberDialogProps> = ({
  open,
  onClose,
  onUpdate,
  selectedMember,
  editRole,
  setEditRole,
  canManageManagers,
  currentUserId,
}) => {
  const { t } = useTranslation();

  const getRoleTranslation = (role: string) => {
    if (role === "owner") return t("preferences.enterprise.role.owner");
    if (role === "manager") return t("preferences.enterprise.role.manager");
    return t("preferences.enterprise.role.member");
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedMember) {
      const isActive = e.target.checked;
      // 選択したメンバーのステータスを更新
      selectedMember.status = isActive ? "active" : "suspended";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("preferences.enterprise.member.editUser")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {selectedMember &&
            t("preferences.enterprise.member.manageRoleStatus", {
              username: selectedMember.user.username,
              email: selectedMember.user.email,
            })}
        </DialogContentText>

        {/* ロールセレクト */}
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          {t("preferences.enterprise.member.role")}:
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="edit-role-select-label">{t("preferences.enterprise.member.role")}</InputLabel>
          <Select
            labelId="edit-role-select-label"
            value={editRole}
            label={t("preferences.enterprise.member.role")}
            onChange={(e) => setEditRole(e.target.value)}
          >
            <MenuItem value="member">{getRoleTranslation("member")}</MenuItem>
            <MenuItem value="manager" disabled={!canManageManagers}>
              {getRoleTranslation("manager")}
            </MenuItem>
            <MenuItem value="owner" disabled={!canManageManagers}>
              {getRoleTranslation("owner")}
            </MenuItem>
          </Select>
        </FormControl>

        {selectedMember?.role === "owner" && (
          <Typography color="warning" sx={{ mt: 1, fontSize: "0.875rem" }}>
            {t("preferences.enterprise.member.lastOwnerWarning")}
          </Typography>
        )}

        {/* ステータス操作 */}
        <Box sx={{ mt: 3 }}>
          {selectedMember && (
            <FormControlLabel
              control={
                <Switch
                  checked={selectedMember.status === "active"}
                  onChange={handleStatusChange}
                  disabled={selectedMember.user.uuid === currentUserId}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1">
                  {t("preferences.enterprise.member.status")}:
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      ml: 1,
                      fontWeight: selectedMember.status === "active" ? "bold" : "normal",
                      color: selectedMember.status === "active" ? "success.main" : "text.secondary",
                    }}
                  >
                    {selectedMember.status === "active"
                      ? t("preferences.enterprise.member.statusActive")
                      : t("preferences.enterprise.member.statusInactive")}
                  </Typography>
                </Typography>
              }
            />
          )}

          {selectedMember?.user.uuid === currentUserId && (
            <Typography color="warning" sx={{ mt: 1, fontSize: "0.875rem" }}>
              {t("preferences.enterprise.member.selfStatusChangeDisabled")}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={onUpdate} color="primary">
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMemberDialog;
